import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectValidation } from 'src/store/selectors/Validation';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { clearValidation } from 'src/store/actions/suites';
import {
  useServiceRequestSchedulingAPI,
  UseServiceRequestSchedulingAPIReturn,
} from './useServiceRequestSchedulingAPI';
import {
  buildCalendlyEmbedUrl,
  toServicePreferences,
} from './schedulingHelpers';
import { IServiceRequestDetail } from 'src/services/api/ServiceRequest/apiContracts';
import { IAvailableServiceTimeDay } from 'src/services/api/UserManagement/apiContracts';

interface UseServiceRequestSchedulingOrchestratorParams {
  serviceRequest: IServiceRequestDetail;
  onClose: () => void;
  onEventScheduled: (data: any) => void;
}

export interface UseServiceRequestSchedulingOrchestratorReturn {
  isLoading: boolean;
  schedulingUrl: string;
  calendlyEmbedUrl: string;
  slotSelected: boolean;
  scheduledAt: any | null;
  availableServiceTimes: IAvailableServiceTimeDay[];

  propertyName: string;
  propertyAddress: string;
  suiteNumber: string;
  homeownerName: string;
  homeownerProfileImage: string;

  onTimeSlotSelection: (selection: string) => Promise<void>;
  onScheduledAtChange: (scheduledAt: any) => void;
  onCalendlyEventScheduled: (event: any) => void;
  onScheduleClick: () => void;
}

export const useServiceRequestSchedulingOrchestrator = ({
  serviceRequest,
  onClose,
  onEventScheduled,
}: UseServiceRequestSchedulingOrchestratorParams): UseServiceRequestSchedulingOrchestratorReturn => {
  const dispatch = useDispatch();

  // TODO: phase N — schedule saves still report completion through the global
  // validation/FormLoader buses; listen until that flow returns a result directly.
  const validation: any = useSelector(selectValidation);
  const formLoader: any = useSelector(selectFormLoader);

  const {
    availableServiceTimes,
    fetchCalendlyLink,
    fetchAvailableServiceTimes,
  }: UseServiceRequestSchedulingAPIReturn = useServiceRequestSchedulingAPI();

  const [isLoadingLink, setIsLoadingLink] = useState<boolean>(true);
  const [schedulingUrl, setSchedulingUrl] = useState<string>('');
  const [slotSelected, setSlotSelected] = useState<boolean>(false);
  const [scheduledAt, setScheduledAt] = useState<any | null>(null);

  const loadCalendlyLink = async () => {
    const result = await fetchCalendlyLink(
      serviceRequest.serviceProvider.email
    );

    setSchedulingUrl(result.schedulingUrl);
    setIsLoadingLink(false);
  };

  useEffect(() => {
    void loadCalendlyLink();
  }, []);

  const closeWhenScheduleValidated = () => {
    if (validation.status !== true || validation.message !== 'Schedule') {
      return;
    }

    onClose();
    dispatch(clearValidation() as any);
  };

  useEffect(() => {
    closeWhenScheduleValidated();
  }, [validation, onClose, dispatch]);

  const onTimeSlotSelection = async (selection: string) => {
    const serviceProviderId: string | undefined =
      serviceRequest?.serviceProvider?.id;

    if (!serviceProviderId) {
      console.warn(
        'ServiceRequestScheduling: time slot selected without a service provider on the service request'
      );

      return;
    }

    const theme: string | null = localStorage.getItem('theme');

    const servicePreferences: string[] = toServicePreferences(
      selection,
      theme
    );

    setSlotSelected(true);

    await fetchAvailableServiceTimes(serviceProviderId, servicePreferences);
  };

  const onScheduledAtChange = (value: any) => {
    setScheduledAt(value);
  };

  const onCalendlyEventScheduled = (event: any) => {
    const uri: string = event?.data?.payload?.event?.uri ?? '';
    const eventId: string = uri.split('/').pop() ?? '';

    if (!eventId) {
      console.warn(
        'ServiceRequestScheduling: Calendly scheduled event carried no event uri'
      );

      return;
    }

    onEventScheduled({ name: 'Scheduled', EventId: eventId });
  };

  const onScheduleClick = () => {
    if (!scheduledAt) {
      console.warn(
        'ServiceRequestScheduling: onScheduleClick called without a scheduled time'
      );

      return;
    }

    onEventScheduled({ name: 'Scheduled', scheduledAt });
  };

  const property: any = serviceRequest?.suite?.property;
  const address: any = property?.propertyAddress;
  const primaryPropertyManager: any = property?.propertyManagers?.[0] ?? null;

  const propertyName: string = property?.name ?? '';
  const propertyAddress: string = `${address?.addressLine1 ?? ''}, ${address?.city ?? ''} ${address?.postalCode ?? ''}`;
  const suiteNumber: string = serviceRequest?.suite?.number ?? '';
  const homeownerName: string = serviceRequest?.suite?.homeOwner?.name ?? '';
  const homeownerProfileImage: string =
    serviceRequest?.suite?.homeOwner?.profileImage ?? '';

  const calendlyEmbedUrl: string = buildCalendlyEmbedUrl(
    schedulingUrl,
    primaryPropertyManager
  );

  const isLoading: boolean = isLoadingLink || Boolean(formLoader);

  return {
    isLoading,
    schedulingUrl,
    calendlyEmbedUrl,
    slotSelected,
    scheduledAt,
    availableServiceTimes,

    propertyName,
    propertyAddress,
    suiteNumber,
    homeownerName,
    homeownerProfileImage,

    onTimeSlotSelection,
    onScheduledAtChange,
    onCalendlyEventScheduled,
    onScheduleClick,
  };
};